import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { connectorsApi } from '../../services/connectorsApi';
import useWorkspaceStore from '../../store/workspace/workspaceStore';
import { useDraftMentionStore } from '../agno/messages/draftMentionStore';

interface PendingApprovalsBadgeProps {
  /** Override the click handler. Defaults to opening the drawer via useDraftMentionStore. */
  onOpen?: () => void;
  className?: string;
}

const POLL_INTERVAL_MS = 30_000;
const STALE_THRESHOLD_SECONDS = 15 * 60;

function isStale(createdAt: number): boolean {
  return Date.now() / 1000 - createdAt > STALE_THRESHOLD_SECONDS;
}

export function PendingApprovalsBadge({ onOpen, className }: PendingApprovalsBadgeProps) {
  const openDrawerFromStore = useDraftMentionStore((s) => s.openDrawer);
  const handleOpen = onOpen ?? (() => openDrawerFromStore());
  const workspaceId =
    useWorkspaceStore((s) => s.workspace?.id) ||
    (typeof window !== 'undefined' ? localStorage.getItem('he_defws') : null);

  const [count, setCount] = useState(0);
  const [hasStale, setHasStale] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCount = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await connectorsApi.getPendingDrafts(String(workspaceId));
      const data = (res as any)?.data;
      const list: Array<{ created_at: number }> = Array.isArray(data?.drafts) ? data.drafts : [];
      setCount(list.length);
      setHasStale(list.some((d) => isStale(d.created_at)));
    } catch (err: any) {
      if (err?.response?.status === 404 || err?.status === 404) {
        setCount(0);
        setHasStale(false);
      }
      // Silently swallow other errors — badge simply stays hidden / at its last value
    }
  }, [workspaceId]);

  // Initial fetch
  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Poll every 30s
  useEffect(() => {
    pollTimerRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [fetchCount]);

  // Refetch on window focus
  useEffect(() => {
    const handler = () => fetchCount();
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, [fetchCount]);

  if (count === 0) return null;

  return (
    <button
      type="button"
      onClick={handleOpen}
      aria-label={`${count} pending approval${count !== 1 ? 's' : ''}`}
      className={cn(
        'inline-flex cursor-pointer items-center rounded-full px-2 py-0.5 text-[11px] font-semibold transition-opacity hover:opacity-80',
        hasStale
          ? 'bg-status-cancelled/[0.12] text-status-cancelled'
          : 'bg-status-in-progress/[0.12] text-status-in-progress',
        className,
      )}
    >
      {count}
    </button>
  );
}

export default PendingApprovalsBadge;
